import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiX } from "react-icons/fi";
import { nudgeDrawer } from "../../utils/modalAttention";
import styles from "./SideDrawer.module.css";
import { WarstwaNadModalem } from "./warstwaNadModalem";

/**
 * [polecenie właściciela 2026-10-01] „widok wysuwanej karty jest pod modalem, więc trzeba poprawić”.
 *
 * MECHANIZM USTERKI (zmierzony): okno modalne (`Modal`, --z-modal 9999) i szuflada (`SideDrawer`, --z-drawer 1100)
 * portalują się OBA do <body>, więc są rodzeństwem i o kolejności decyduje wyłącznie z-index. Prop `nadModalem`
 * był OPT-IN — każda szuflada wołana z karty zgłoszenia musiała go pamiętać. Szuflada „Automapuj” dodana do karty
 * 1.10 go nie miała i wysunęła się POD kartę.
 *
 * NAPRAWA NA POZIOMIE WARSTWY, nie pojedynczej szuflady: okno modalne obejmuje swoją treść `<SzufladyNadModalem>`,
 * a KAŻDA szuflada w tym poddrzewie Reacta (portal zachowuje kontekst) sama staje na --z-drawer-over-modal.
 * Szuflady na zwykłych stronach kontekstu nie widzą — zostają na --z-drawer i dalej dają się przykryć oknem.
 */
/* Kontekst mieszka w ./warstwaNadModalem — czytają go też pola z listą w portalu (kalendarz, multiselect). */
export { WarstwaNadModalem };

/** Opakowanie treści okna modalnego: każda szuflada otwarta z jego wnętrza stoi NAD nim. */
export const SzufladyNadModalem = ({ children }) => (
    <WarstwaNadModalem.Provider value={true}>{children}</WarstwaNadModalem.Provider>
);

/**
 * SideDrawer — wysuwany panel z prawej krawedzi.
 *
 * DROP-IN ZAMIENNIK dla components/Modal/Modal: przyjmuje TE SAME propsy
 * (isOpen, onClose, title, children, width), wiec zamiana modala na panel to podmiana importu —
 * formularz w srodku zostaje nietkniety. Dodatkowo obsluguje `icon`, `subtitle` i `footer`.
 *
 * Uwaga na `width`: w modalu byla to szerokosc okna wysrodkowanego (czesto 1000-1500px).
 * Tu jest to szerokosc panelu przy krawedzi, wiec wartosc jest przycinana do rozsadnego zakresu
 * (min 380px, max 96vw) — inaczej "panel" zajalby caly ekran i nie roznil sie od modala.
 *
 * Zamkniecie WYLACZNIE przez X / przyciski w stopce. Klik w tlo NIE zamyka — daje potrzasniecie
 * (warunek W-10 design systemu: chroni przed utrata wpisanych danych).
 *
 * `nadModalem` [polecenie właściciela 2026-09-10] — szuflada otwierana Z WNĘTRZA okna modalnego.
 * Panel i modal portalują się OBA do <body>, więc bez tego szuflada na --z-drawer (1100) wysuwa się
 * POD oknem na --z-modal (9999) i nie widać jej wcale. Prop podnosi wyłącznie TĘ szufladę na własną,
 * nazwaną warstwę --z-drawer-over-modal; domyślnie nic się nie zmienia, bo większość szuflad stoi na
 * zwykłej stronie i MUSI dać się przykryć oknem modalnym.
 * [2026-10-01] Ten sam wariant włącza się SAM wewnątrz `<SzufladyNadModalem>` (kontekst `WarstwaNadModalem`, patrz
 * wyżej) — jawny prop zostaje dla szuflad montowanych poza poddrzewem okna.
 *
 * FOKUS STARTOWY [ui1006, poprawka po przeglądzie KIT-C 2026-10-06]: po otwarciu panel (`aside`, `tabIndex=-1`) dostaje
 * fokus — chyba że treść już go ustawiła sama (`autoFocus` pola, własny `focus()` w efekcie dziecka: efekty dzieci biegną
 * PRZED efektem szuflady, więc sprawdzamy, czy aktywny element nie leży już w panelu). Bez tego fokus klawiatury zostawał
 * pod nakładką `aria-modal` (np. na `h1` strony), a Tab wędrował po pasku i treści POD szufladą. Zachowanie dotychczasowych
 * szuflad z własnym fokusem jest bez zmian; `tabIndex=-1` nie wchodzi do kolejności Tab i nie liczy się jako kontrolka.
 *
 * `tloTresci` [polecenie właściciela 2026-10-02, EB-5] — OPCJONALNE tło treści szuflady. W jasnym motywie
 * `--color-bg-primary` i `--color-bg-secondary` to ten sam biały, więc karta z cieniem postawiona w treści
 * szuflady była niewidoczna i panele sprzętu ITAM musiały rysować ramkę na każdej sekcji. `tloTresci="tertiary"`
 * kładzie treść na `--color-bg-tertiary` i karty odcinają się same. Bez propa DOM i wygląd są IDENTYCZNE jak
 * dotąd — dziesiątki innych szuflad tego nie widzą (zapadka SideDrawer/__tests__/tloTresciOptIn.ea7.test.js).
 */
const SideDrawer = ({
    isOpen,
    onClose,
    title,
    subtitle,
    icon,
    children,
    footer,
    width = "560px",
    nadModalem = false,
    pelnaSzerokosc = false,
    tloTresci = null,
}) => {
    /* [E-5, polecenie właściciela 2026-09-11: „poszerz wysuwane okno z prawej strony na całą główną"]

       Tryb „na całą główną" NIE jest 100vw. Panel ma zakryć obszar treści, a zostawić widoczny pasek
       boczny — inaczej człowiek traci orientację, gdzie jest, i panel przestaje być panelem.
       Lewej krawędzi nie da się tu wpisać na sztywno: pasek boczny bywa zwinięty, a szuflada
       portaluje się do <body> i nie widzi klasy układu (nazwy klas modułowych są zahaszowane).
       Dlatego MIERZYMY element oznaczony `data-obszar="tresc-glowna"`. */
    const [lewaKrawedz, setLewaKrawedz] = useState(null);
    /* Jawny prop ALBO kontekst okna modalnego — szuflada wewnątrz karty nie musi już pamiętać propsa. */
    const zOknaModalnego = useContext(WarstwaNadModalem);
    const nadOknem = nadModalem || zOknaModalnego;
    const panelRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        const panel = panelRef.current;
        if (!panel) return;
        const aktywny = document.activeElement;
        if (aktywny && aktywny !== document.body && panel.contains(aktywny)) return;
        try { panel.focus({ preventScroll: true }); } catch { /* panel właśnie znika — fokus zostaje */ }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !pelnaSzerokosc) return undefined;

        const glowna = document.querySelector('[data-obszar="tresc-glowna"]');
        if (!glowna) {
            // Brak uchwytu (np. podgląd poza układem aplikacji) — wracamy do zwykłej szerokości
            // zamiast zgadywać. Zgadywanie kończy się panelem wchodzącym na pasek boczny.
            setLewaKrawedz(null);
            return undefined;
        }

        const zmierz = () => setLewaKrawedz(Math.max(0, Math.round(glowna.getBoundingClientRect().left)));
        zmierz();

        window.addEventListener("resize", zmierz);
        // Pasek boczny zwija się z animacją, więc sama zmiana rozmiaru okna nie wystarcza.
        const obserwator = typeof ResizeObserver !== "undefined" ? new ResizeObserver(zmierz) : null;
        if (obserwator) obserwator.observe(glowna);

        return () => {
            window.removeEventListener("resize", zmierz);
            if (obserwator) obserwator.disconnect();
        };
    }, [isOpen, pelnaSzerokosc]);

    if (!isOpen) return null;

    // Modale bywaly bardzo szerokie (np. 1500px na podgladzie faktury). Jako panel boczny
    // ograniczamy do 96vw, zeby zawsze bylo widac, ze to wysuwana karta, a nie pelny ekran.
    const drawerStyle = pelnaSzerokosc && lewaKrawedz !== null
        ? {
            width: `calc(100vw - ${lewaKrawedz}px)`,
            minWidth: "min(380px, 100vw)",
            maxWidth: "100vw",
        }
        : {
            width,
            minWidth: "min(380px, 100vw)",
            maxWidth: "96vw",
        };

    // nudgeDrawer sam sprawdza, czy klik trafil w SAMO tlo (e.target === e.currentTarget).
    const klasaOverlay = nadOknem
        ? `${styles.overlay} ${styles.overlayNadModalem}`
        : styles.overlay;
    const klasaPanelu = nadOknem
        ? `${styles.drawer} ${styles.drawerNadModalem}`
        : styles.drawer;
    const klasaTresci = tloTresci === "tertiary"
        ? `${styles.body} ${styles.bodyTertiary}`
        : styles.body;

    return createPortal(
        <div className={klasaOverlay} onClick={nudgeDrawer}>
            <aside
                ref={panelRef}
                className={klasaPanelu}
                style={drawerStyle}
                role="dialog"
                aria-modal="true"
                aria-label={typeof title === "string" ? title : undefined}
                tabIndex={-1}
            >
                <header className={styles.header}>
                    <div className={styles.headerLeft}>
                        {icon && <span className={styles.headerIcon}>{icon}</span>}
                        <div className={styles.headerTitles}>
                            <h2 className={styles.title}>{title}</h2>
                            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
                        </div>
                    </div>
                    <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Zamknij">
                        <FiX size={17} />
                    </button>
                </header>

                {/* Szuflada otwarta Z WNĘTRZA szuflady stojącej nad oknem musi stanąć co najmniej tak samo wysoko —
                    inaczej wysunęłaby się pod nią (--z-drawer 1100 pod --z-drawer-over-modal). */}
                <WarstwaNadModalem.Provider value={nadOknem}>
                    <div className={klasaTresci}>{children}</div>
                    {footer && <div className={styles.footer}>{footer}</div>}
                </WarstwaNadModalem.Provider>
            </aside>
        </div>,
        document.body
    );
};

export default SideDrawer;
